import { Component, isDevMode, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { MatIcon } from '@angular/material/icon'
import { Router } from '@angular/router'
import { ParagraphDirective } from '@app-ui/directives'
import { ButtonDirective } from '@app/domains/ui/directives/button/button.directive'
import { HeadingDirective } from '@app/domains/ui/directives/heading/heading.directive'
import { DefaultLayoutComponent } from '@app/domains/ui/layouts/default/default.layout.component'
import { toBase64 } from '@app/lib/toBase64'
import { FaceMatchService, ErrorService, ErrorType } from '@app/services'
import { RfidScannerService } from '@app/services/rfid-scanner/rfid-scanner.service'
import { UnattendendCheckInService } from '@app/services/unattendend-check-in/unattendend-check-in.service'
import Bugsnag from '@bugsnag/js'
import { firstValueFrom } from 'rxjs'

@Component({
    selector: 'app-welcome-page',
    imports: [
        HeadingDirective,
        DefaultLayoutComponent,
        MatIcon,
        ButtonDirective,
        ParagraphDirective,
    ],
    templateUrl: './welcome-page.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './welcome-page.component.scss',
})
export class WelcomePageComponent implements OnInit {

    constructor(
        private readonly unattendedCheckInService: UnattendendCheckInService,
        private readonly errorService: ErrorService,
        private readonly faceMatchService: FaceMatchService,
        private readonly rfidScannerService: RfidScannerService,
        private readonly router: Router,
    ) {
    }

    public async ngOnInit(): Promise<void> {
        try {
            const scannerInfo = await firstValueFrom(this.rfidScannerService.getScannerInfo())

            if (! scannerInfo.data?.deviceToken) {
                throw new Error('No device token found in getScannerInfo')
            }

            this.unattendedCheckInService.setXDeviceToken(scannerInfo.data?.deviceToken)

            Bugsnag.addMetadata('EntranceID', {
                id: scannerInfo.data?.deviceToken,
            })

            await this.pollRfidScanner()
        } catch (error: any) {
            Bugsnag.notify({
                name: 'GetScannerInfo',
                message: error.message || '',
            })
            console.error(error)
        }
    }

    public async pollRfidScanner(): Promise<void> {
        try {
            const scanResult = await firstValueFrom(this.rfidScannerService.manualScan(60))
            if (! scanResult.success) {
                await this.pollRfidScanner()
            } else {
                await this.startFaceScanProcess()
            }
        } catch (error) {
            console.error(error)
        }
    }

    public async startFaceScanProcess(rfidCode?: string) {
        try {
            const rfidPass = await firstValueFrom(this.rfidScannerService.getData())

            if (! rfidPass.data?.code) {
                if (! rfidCode) {
                    throw new Error('No code found on RFID')
                }
            }

            const response = await this.unattendedCheckInService.startUnattendedCheckIn({
                code: rfidCode ? rfidCode : rfidPass.data!.code,
            })

            if (! response || response.visitor.visitorIdentifications.length <= 0) {
                return
            }

            let preferedDocument = response.visitor.visitorIdentifications.find((document) => document.isPreferred)

            if (! preferedDocument) {
                console.error('No preferred visitor identification found')
                Bugsnag.notify('No preferred visitor identification found')
                await this.errorService.addError(ErrorType.IDENTITY)
                return
            }

            if (! preferedDocument.image) {
                console.error('No image found on prefered visitor identification')
                Bugsnag.notify('No image found on prefered visitor identification')
                await this.errorService.addError(ErrorType.IDENTITY)
                return
            }

            let base64 = await toBase64(preferedDocument.image)
            await this.faceMatchService.setReferenceImage(base64 as string)

            await this.router.navigate(['/intro'])
        } catch (error) {
            console.error(error)
            await this.errorService.addError(ErrorType.IDENTITY)
        }
    }

    public async simulateCard(card: 'a' | 'b' | 'c'): Promise<void> {
        let rfidCode
        switch (card) {
            case 'a':
                rfidCode = '40-CA-EC-03@3B-8F-80-01-80-4F-0C-A0-00-00-03-06-03-00-03-00-00-00-00-68'
                break
            case 'b':
                rfidCode = '30-56-95-03@3B-8F-80-01-80-4F-0C-A0-00-00-03-06-03-00-03-00-00-00-00-68'
                break
            case 'c':
                rfidCode = '30-C9-EC-03@3B-8F-80-01-80-4F-0C-A0-00-00-03-06-03-00-03-00-00-00-00-68'
                break
        }
        await this.startFaceScanProcess(rfidCode)
    }

    protected readonly isDevMode = isDevMode
}
